import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/utils/cn'

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

interface PaginationProps {
  /** Zero-based page index. */
  page: number
  size: number
  totalElements: number
  totalPages: number
  onPageChange: (page: number) => void
  /** Shows the page-size select when provided. */
  onSizeChange?: (size: number) => void
  sizeOptions?: readonly number[]
  className?: string
  /** Noun for the range label, e.g. "records". */
  itemLabel?: string
}

/** Page numbers to show around the current one; null marks a gap. */
export function pageWindow(page: number, totalPages: number): (number | null)[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i)
  const pages = new Set([0, totalPages - 1, page - 1, page, page + 1])
  const sorted = [...pages].filter((p) => p >= 0 && p < totalPages).sort((a, b) => a - b)
  const result: (number | null)[] = []
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push(null)
    result.push(p)
  })
  return result
}

export function Pagination({
  page,
  size,
  totalElements,
  totalPages,
  onPageChange,
  onSizeChange,
  sizeOptions = PAGE_SIZE_OPTIONS,
  className,
  itemLabel = 'items',
}: PaginationProps) {
  if (totalElements === 0) return null
  const from = page * size + 1
  const to = Math.min(totalElements, (page + 1) * size)
  const isFirst = page <= 0
  const isLast = page >= totalPages - 1

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex flex-wrap items-center justify-between gap-3 pt-3 text-sm text-muted-foreground', className)}
    >
      <div className="flex items-center gap-3">
        <span className="tabular-nums">
          {from}–{to} of {totalElements} {itemLabel}
        </span>
        {onSizeChange && (
          <Select value={String(size)} onValueChange={(value) => onSizeChange(Number(value))}>
            <SelectTrigger className="h-8 w-[5.5rem]" aria-label="Items per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sizeOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isFirst}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </Button>
          {pageWindow(page, totalPages).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="px-1">
                …
              </span>
            ) : (
              <Button
                key={p}
                type="button"
                size="sm"
                variant={p === page ? 'secondary' : 'ghost'}
                aria-current={p === page ? 'page' : undefined}
                className="min-w-9 tabular-nums"
                onClick={() => onPageChange(p)}
              >
                {p + 1}
              </Button>
            )
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isLast}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight />
          </Button>
        </div>
      )}
    </nav>
  )
}
